// Posts manifest — the unified editorial content model (articles, videos,
// podcasts) shared across all four pillars.
//
// One Post = one topic. A topic can be syndicated across formats: an article
// version, a video version (embedded from YouTube), and a podcast version
// (embedded from Captivate). Each format is optional; only the formats that
// exist render on the topic detail page, in the fixed order Video → Podcast
// → Article.
//
// ─── How to add a post ─────────────────────────────────────────────────────
//
// 1. Pick the primary pillar: tech | dance | media | think.
//    The pillar decides the canonical URL and the pillar landing where the
//    post's PostCard shows in "Latest posts."
//
// 2. Add ONE entry to the `posts` array below. Required fields:
//    id, slug, pillar, title, excerpt, publishedAt, tags, formats, published.
//
// 3. Add 1+ format objects to `formats`. Each format has its own publishedAt
//    so a topic can grow over time (article Jan 1, video Feb 10, podcast Mar 3).
//
// EXAMPLE — single-format article (the current pattern):
//
//   {
//     id: "kizomba-systems",
//     slug: "kizomba-systems",
//     pillar: "dance",
//     title: "Kizomba as Systems Thinking",
//     excerpt: "What ten years of teaching taught me about scaling any complex practice.",
//     publishedAt: "2026-08-01",
//     tags: ["teaching", "systems", "community"],
//     published: true,
//     formats: [
//       {
//         type: "article",
//         markdownPath: "kizomba-systems.md",       // relative to data/posts/
//         readTimeMin: readingTimeMin(kizombaSystemsContent),
//         publishedAt: "2026-08-01",
//       },
//     ],
//   }
//
//   And at the top of this file, add:
//     import kizombaSystemsContent from "./posts/kizomba-systems.md?raw";
//     // and include in the `articleContent` map below.
//
// EXAMPLE — multi-format syndicated topic (article + video + podcast):
//
//   {
//     id: "why-i-moved-to-austin",
//     slug: "why-i-moved-to-austin",
//     pillar: "think",
//     title: "Why I Moved to Austin",
//     excerpt: "Ten years in one city taught me what I actually value.",
//     publishedAt: "2026-08-15",
//     tags: ["culture", "self", "systems"],
//     published: true,
//     formats: [
//       {
//         type: "video",
//         provider: "youtube",
//         // Paste the full YouTube URL and let youtubeIdFromUrl() extract:
//         //   const id = youtubeIdFromUrl("https://youtube.com/watch?v=abc123");
//         videoId: "abc123",           // the 11-character ID
//         durationSec: 720,            // 12:00
//         publishedAt: "2026-08-15",
//       },
//       {
//         type: "podcast",
//         provider: "captivate",
//         // Paste the Captivate shareable URL and let captivateIdFromUrl() extract:
//         //   const id = captivateIdFromUrl("https://player.captivate.fm/episode/abc-123-def");
//         episodeId: "abc-123-def",
//         durationSec: 2520,           // 42:00
//         publishedAt: "2026-08-20",
//       },
//       {
//         type: "article",
//         markdownPath: "why-i-moved-to-austin.md",
//         readTimeMin: readingTimeMin(whyIMovedContent),
//         publishedAt: "2026-08-15",
//       },
//     ],
//   }
//
// EXAMPLE — cross-pillar surfacing (rare, ~20% of posts):
//
//   {
//     ...same fields as above...
//     pillar: "think",                 // canonical home → /think/some-slug
//     crossPillars: ["dance"],         // also shows as a "From /think" card
//                                      // on the Dance landing's "Related"
//                                      // section, but the URL stays /think.
//   }
//
// ─── Rules ─────────────────────────────────────────────────────────────────
//
// - Canonical URL is always /{primary-pillar}/{slug} — resolved via postUrl().
// - Slugs must be unique WITHIN a pillar; they can repeat across pillars.
// - Do NOT hardcode a YouTube embed URL or Captivate embed URL. Just store
//   the ID; the renderer builds the embed URL via youtubeEmbedUrl() /
//   captivateEmbedUrl().
// - readTimeMin() computes ~200 wpm reading time — call it inline with the
//   imported markdown so the number stays in sync with edits.

// ── Types ────────────────────────────────────────────────────────────────────

export type PillarSlug = "tech" | "dance" | "media" | "think";

export type FormatType = "article" | "video" | "podcast";

// Fixed global format render order for the topic detail page.
// Sections stack in this order; missing formats are skipped entirely.
export const FORMAT_ORDER: FormatType[] = ["video", "podcast", "article"];

interface ArticleFormat {
  type: "article";
  markdownPath: string; // path relative to data/posts/ (e.g. "kizomba-systems.md")
  readTimeMin: number;
  publishedAt: string; // ISO date this format went live
}

interface VideoFormat {
  type: "video";
  provider: "youtube";
  videoId: string; // extracted from URL via youtubeIdFromUrl()
  durationSec: number;
  publishedAt: string;
}

interface PodcastFormat {
  type: "podcast";
  provider: "captivate";
  episodeId: string; // extracted from Captivate URL via captivateIdFromUrl()
  durationSec: number;
  showNotesPath?: string; // optional companion .md in data/posts/
  publishedAt: string;
}

export type PostFormat = ArticleFormat | VideoFormat | PodcastFormat;

export interface Post {
  id: string;
  slug: string;
  pillar: PillarSlug;
  crossPillars?: PillarSlug[]; // secondary pillars where this appears as a "Related" card
  title: string;
  excerpt: string; // 1-2 sentence teaser for cards
  publishedAt: string; // ISO date — the topic's canonical publish date
  coverImage?: string; // Cloudinary URL
  tags: string[]; // freeform (e.g. "systems", "community")
  formats: PostFormat[]; // 1+ format renders
  published: boolean; // draft toggle
}

// ── Manifest ─────────────────────────────────────────────────────────────────
// Markdown imports use Vite's ?raw suffix — the .md files must live at
// data/posts/<name>.md relative to this file.

import buildingPortfolioContent from "./posts/building-this-portfolio.md?raw";
import helloWorldContent from "./posts/hello-world.md?raw";

// Article markdown content is imported at build time and stored in this map,
// keyed by markdownPath. The renderer looks up the content by path.
export const articleContent: Record<string, string> = {
  "building-this-portfolio.md": buildingPortfolioContent,
  "hello-world.md": helloWorldContent,
};

export const posts: Post[] = [
  {
    id: "building-this-portfolio",
    slug: "building-this-portfolio",
    pillar: "think",
    title: "How I Built This Portfolio",
    excerpt:
      "A breakdown of the Rails 8 + React 19 + Inertia.js stack, the design decisions, and what I'd do differently next time.",
    publishedAt: "2026-02-20",
    tags: ["technical", "rails", "react", "meta"],
    published: true,
    formats: [
      {
        type: "article",
        markdownPath: "building-this-portfolio.md",
        readTimeMin: readingTimeMin(buildingPortfolioContent),
        publishedAt: "2026-02-20",
      },
    ],
  },
  {
    id: "hello-world",
    slug: "hello-world",
    pillar: "think",
    title: "Hello, World",
    excerpt:
      "Welcome to my blog — where I write about code, creativity, community building, and the intersections between them.",
    publishedAt: "2026-02-15",
    tags: ["general", "updates"],
    published: true,
    formats: [
      {
        type: "article",
        markdownPath: "hello-world.md",
        readTimeMin: readingTimeMin(helloWorldContent),
        publishedAt: "2026-02-15",
      },
    ],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** All published posts, newest-first by canonical publishedAt. */
export function getPublishedPosts(): Post[] {
  return posts
    .filter((p) => p.published)
    .sort(
      (a, b) =>
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );
}

/** Published posts where the given pillar is primary. */
export function getPostsByPillar(pillar: PillarSlug): Post[] {
  return getPublishedPosts().filter((p) => p.pillar === pillar);
}

/** Published posts where the given pillar appears in crossPillars (not primary). */
export function getCrossPillarPosts(pillar: PillarSlug): Post[] {
  return getPublishedPosts().filter(
    (p) => p.pillar !== pillar && p.crossPillars?.includes(pillar),
  );
}

/** Lookup a single post by (pillar, slug) — used for topic detail pages. */
export function getPostBySlug(
  pillar: PillarSlug,
  slug: string,
): Post | undefined {
  return posts.find(
    (p) => p.published && p.pillar === pillar && p.slug === slug,
  );
}

/** All published posts within a pillar that have a specific format available. */
export function getPostsByPillarAndFormat(
  pillar: PillarSlug,
  format: FormatType,
): Post[] {
  return getPostsByPillar(pillar).filter((p) =>
    p.formats.some((f) => f.type === format),
  );
}

/** Extract the article body markdown for a Post, or undefined if it has none. */
export function articleBodyFor(post: Post): string | undefined {
  const article = post.formats.find(
    (f): f is ArticleFormat => f.type === "article",
  );
  if (!article) return undefined;
  return articleContent[article.markdownPath];
}

/**
 * URL for a Post's canonical detail page.
 *
 * Asymmetric by design — /tech/:slug and /media/:slug are already taken by
 * projects and gigs respectively, so posts on those pillars nest under
 * /:pillar/posts/:slug. Think and Dance have no such conflict and use the
 * flatter /:pillar/:slug pattern (Think preserves existing blog URLs).
 */
export function postUrl(post: Pick<Post, "pillar" | "slug">): string {
  if (post.pillar === "tech" || post.pillar === "media") {
    return `/${post.pillar}/posts/${post.slug}`;
  }
  return `/${post.pillar}/${post.slug}`;
}

// ── URL / ID extraction ──────────────────────────────────────────────────────

/**
 * Extract a YouTube video ID from any of the common URL shapes:
 *   https://www.youtube.com/watch?v=abc123
 *   https://youtu.be/abc123
 *   https://www.youtube.com/embed/abc123
 * Returns null if no ID is recognizable.
 */
export function youtubeIdFromUrl(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/** Build a YouTube embed URL from a video ID. */
export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}

/**
 * Extract a Captivate episode ID from a shareable URL like:
 *   https://player.captivate.fm/episode/abc-123-def-456
 *   https://ogarocious.captivate.fm/episode/abc-123-def-456
 * Returns null if no ID is recognizable.
 */
export function captivateIdFromUrl(url: string): string | null {
  const m = url.match(/captivate\.fm\/episode\/([A-Za-z0-9-]+)/);
  return m ? m[1] : null;
}

/** Build a Captivate embed URL from an episode ID. */
export function captivateEmbedUrl(episodeId: string): string {
  return `https://player.captivate.fm/episode/${episodeId}`;
}

// ── Content utilities ────────────────────────────────────────────────────────

/** Reading time in minutes at ~200 wpm. Used at import time for article formats. */
export function readingTimeMin(markdown: string): number {
  const words = markdown.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
}

/** Format seconds as "H:MM:SS" or "M:SS" — for durations on cards. */
export function formatDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
